/* Mug gallery in the browser: paints every MugRenderer view onto its canvas.
   Layers are theme assets (window.MugMaterial.assets, from the section); the
   artwork comes through the aiapi design-image proxy because GCS sends no CORS. */
(function () {
  'use strict'
  var engine = window.MugRenderer, N = engine.size
  var cache = { uv: {}, base: {}, art: {}, layer: new Map() }
  var PROXY = 'https://aiapi.macmerise.com/api/design-image?file='
  var GCS = 'https://storage.googleapis.com/ai-customizer-v2/designs/'
  var CDN = 'https://cdn.macmerise.com/customiser/designs/'
  // Design pixels go through the API proxy: Front Door caches the CDN copy without CORS headers when a plain <img> fetched it first, and a canvas cannot read it.
  var viaProxy = function (url) { var base = url.indexOf(CDN) === 0 ? CDN : url.indexOf(GCS) === 0 ? GCS : null; return base ? PROXY + encodeURIComponent(url.slice(base.length)) : url }

  function asset(name) { var a = window.MugMaterial && window.MugMaterial.assets; if (!a || !a[name]) throw new Error('Missing mug asset ' + name); return a[name] }
  function canvas(w, h) { var c = document.createElement('canvas'); c.width = w || N; c.height = h || N; return c }
  function context(c) { return c.getContext('2d', { willReadFrequently: true }) }
  function image(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image(); img.crossOrigin = 'anonymous'
      img.onload = function () { resolve(img) }; img.onerror = function () { reject(new Error('Could not load ' + url)) }
      img.src = url
    })
  }
  // Coordinate maps and masks must arrive lossless: the Shopify CDN serves a lossy
  // WebP to browsers that accept it (Vary: Accept), which shifts values by a few
  // units and ripples the warp. Ask for the PNG bytes and decode them ourselves.
  function exact(url) {
    return fetch(url, { headers: { Accept: 'image/png' } }).then(function (r) { if (!r.ok) throw new Error('Could not load ' + url); return r.blob() })
      .then(function (blob) { return new Promise(function (resolve, reject) {
        var u = URL.createObjectURL(blob), img = new Image()
        img.onload = function () { URL.revokeObjectURL(u); resolve(img) }
        img.onerror = function () { URL.revokeObjectURL(u); reject(new Error('Could not decode ' + url)) }
        img.src = u
      }) })
  }
  function pixels(img, w, h) { var c = canvas(w || img.naturalWidth, h || img.naturalHeight); var g = context(c); g.imageSmoothingQuality = 'high'; g.drawImage(img, 0, 0, c.width, c.height); return g.getImageData(0, 0, c.width, c.height) }
  function cached(store, key, load) { if (!store[key]) store[key] = load().catch(function (e) { delete store[key]; throw e }); return store[key] }

  var uv = function (source, slot) { return cached(cache.uv, source + '-' + slot, function () { return exact(asset('mug-uv-' + source + '-' + slot + '.png')).then(function (img) { return engine.prepare(pixels(img, engine.uv.width, engine.uv.height)) }) }) }
  var base = function (colour, source) { return cached(cache.base, colour + '-' + source, function () { return image(asset('mug-' + colour + '-' + source + '.webp')) }) }
  var art = function (url) { return cached(cache.art, url, function () { return image(viaProxy(url)).then(function (img) { return pixels(img) }) }) }
  var extra = function (name) { return cached(cache.base, name, function () { return (/\.png$/.test(name) ? exact : image)(asset(name)) }) }

  // The warped artwork for one view and one fit does not depend on the colour: keep it per
  // design, a few fits deep (each entry is a full canvas). A drag asks for one fit per frame
  // and keeps none of them (nocache); the settle repaint fills the cache at the final fit.
  var LAYERS_KEPT = 16
  function layer(view, url, transform, nocache) {
    var f = engine.fit(transform), key = view.id + '|' + url + '|' + f.scale + ',' + f.translateX + ',' + f.translateY
    if (cache.layer.has(key)) { var hit = cache.layer.get(key); cache.layer.delete(key); cache.layer.set(key, hit); return hit }
    var made = Promise.all([Promise.all(view.slots.map(function (slot) { return uv(view.source, slot) })), art(url)]).then(function (parts) {
      var out = canvas(); context(out).putImageData(new ImageData(engine.artwork(view, parts[0], parts[1], 1, f), N, N), 0, 0); return out
    })
    if (nocache) return made
    cache.layer.set(key, made.catch(function (e) { cache.layer.delete(key); throw e }))
    while (cache.layer.size > LAYERS_KEPT) cache.layer.delete(cache.layer.keys().next().value)
    return made
  }

  function paint(target, view, colour, ink, alpha) {
    return base(colour, view.source).then(function (lit) {
      var mug = canvas(), g = context(mug)
      g.drawImage(lit, 0, 0, N, N)
      if (ink) { g.globalCompositeOperation = 'multiply'; g.globalAlpha = alpha === undefined ? 1 : alpha; g.drawImage(ink, 0, 0); g.globalCompositeOperation = 'source-over'; g.globalAlpha = 1 }
      var steps = []
      if (view.features) steps.push(extra('mug-features-3.svg').then(function (band) { g.drawImage(band, 0, 0, N, N * band.naturalHeight / band.naturalWidth) }))
      if (view.lifestyle) steps.push(Promise.all([extra('mug-scene.webp'), extra('mug-mask.png')]).then(function (parts) {
        var s = N / engine.psd, box = view.lifestyle
        g.globalCompositeOperation = 'destination-in'; g.drawImage(parts[1], 0, 0, N, N); g.globalCompositeOperation = 'source-over'
        var scene = canvas(), sg = context(scene); sg.drawImage(parts[0], 0, 0, N, N); sg.drawImage(mug, box.x * s, box.y * s, box.side * s, box.side * s); mug = scene
      }))
      return Promise.all(steps).then(function () { var t = target.getContext('2d'); t.clearRect(0, 0, N, N); t.drawImage(mug, 0, 0); target.dataset.ready = 'true' })
    })
  }

  /**
   * @param {Element} container - holds one canvas[data-view] per view, hero first
   * @param {Function} getState - () => { colour, designUrl|null, transform|null, pending: { url, opacity }|null }
   *   `transform` is where the artwork sits on the wrap (MugRenderer.fit); `pending` is the panel
   *   the hero wraps while the design draws (no designUrl yet), at that opacity.
   */
  function mount(container, getState) {
    var token = 0, frame = 0, disposed = false, heroInk = null
    var canvases = Array.prototype.slice.call(container.querySelectorAll('canvas[data-view]'))
    canvases.forEach(function (c) { c.width = c.height = N })
    async function draw() {
      var run = ++token, state = getState(), colour = engine.find(state.colour)
      if (!colour) throw new Error('Unsupported mug colour')
      for (var i = 0; i < canvases.length; i++) {
        if (run !== token || disposed) return false
        var view = engine.view(canvases[i].dataset.view)
        var pending = !state.designUrl && i === 0 ? state.pending : null
        var ink = state.designUrl ? await layer(view, state.designUrl, state.transform) : pending ? await layer(view, pending.url) : null
        if (run !== token || disposed) return false
        await paint(canvases[i], view, colour.key, ink, pending ? pending.opacity : 1)
        if (i === 0) heroInk = ink
        // Let the page breathe between views: the warp is a full-frame pixel loop.
        await new Promise(function (r) { setTimeout(r, 0) })
      }
      container.dataset.ready = 'true'
      return true
    }
    function update() { cancelAnimationFrame(frame); frame = requestAnimationFrame(function () { draw().catch(function (e) { container.dataset.error = e.message; console.error('Mug preview:', e) }) }) }
    // The hero alone: the pending pulse and a drag or zoom call this per tick. One paint runs at a
    // time and the latest state wins, so a slow phone drops frames instead of piling them up.
    var heroBusy = false, heroAgain = false
    function paintHero() {
      if (heroBusy) { heroAgain = true; return Promise.resolve() }
      if (disposed || !canvases.length) return Promise.resolve()
      heroBusy = true
      var state = getState(), colour = engine.find(state.colour), view = engine.view(canvases[0].dataset.view)
      var ink = !colour ? Promise.resolve(null) : state.designUrl ? layer(view, state.designUrl, state.transform, true) : state.pending ? layer(view, state.pending.url) : Promise.resolve(null)
      return ink.then(function (ink) { if (!disposed && colour) { heroInk = ink; return paint(canvases[0], view, colour.key, ink, state.designUrl || !state.pending ? 1 : state.pending.opacity) } })
        .catch(function (e) { console.error('Mug preview:', e) })
        .then(function () { heroBusy = false; if (heroAgain) { heroAgain = false; paintHero() } })
    }
    // Is there printed artwork under this point of the hero (fractions of its width and height)?
    // The editor starts a drag only on the print; elsewhere the browser swipes or scrolls.
    function hitArt(fx, fy) {
      if (!heroInk) return false
      var x = Math.max(0, Math.min(N - 1, Math.round(fx * N))), y = Math.max(0, Math.min(N - 1, Math.round(fy * N)))
      try { return context(heroInk).getImageData(x, y, 1, 1).data[3] > 24 } catch (_e) { return false }
    }
    update()
    return {
      update: update,
      paintHero: paintHero,
      hitArt: hitArt,
      flush: function () { cancelAnimationFrame(frame); return draw() },
      destroy: function () { disposed = true; cancelAnimationFrame(frame) },
    }
  }

  window.MugBrowser = { mount: mount }
})()
